import { Question } from "@/types/test";
import { QuestionListSection } from "../test-creator-sections/QuestionListSection";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Database, Plus, AlertCircle } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useState } from "react";
import { QuestionBankDialog } from "../QuestionBankDialog";
import { GenerateAdditionalQuestionsDialog } from "../GenerateAdditionalQuestionsDialog";
import { validateQuestion, getValidationErrors } from "../utils/questionValidation";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface QuestionMakerScreenProps {
  questions: Question[];
  onAddQuestion: () => string;
  onUpdateQuestion: (id: string, updates: Partial<Question>) => void;
  onDeleteQuestion: (id: string) => void;
  onRegenerateQuestion?: (id: string, feedback: string) => Promise<void>;
  onAddMultipleQuestions?: (questions: Question[]) => void;
  onNext: () => void;
  onPrevious: () => void;
  jobContext?: {
    title?: string;
    description?: string;
    keySkills?: string[];
    yearsOfExperience?: string;
  };
}

export const QuestionMakerScreen = ({
  questions,
  onAddQuestion,
  onUpdateQuestion,
  onDeleteQuestion,
  onRegenerateQuestion,
  onAddMultipleQuestions,
  onNext,
  onPrevious,
  jobContext,
}: QuestionMakerScreenProps) => {
  const isMobile = useIsMobile();
  const [isQuestionBankOpen, setIsQuestionBankOpen] = useState(false);

  // Validate all questions
  const allQuestionsValid = questions.length > 0 && questions.every(validateQuestion);
  const invalidQuestions = questions.filter(q => !validateQuestion(q));

  const getNextButtonTooltip = () => {
    if (questions.length === 0) {
      return "Add at least one question to proceed";
    }
    if (invalidQuestions.length > 0) {
      return `${invalidQuestions.length} question(s) need completion: question text, time limit, marks, and markscheme required`;
    }
    return "";
  };

  const handleAddFromQuestionBank = (question: Question) => {
    console.log('Question received from bank:', question);
    const updatedQuestion = {
      ...question,
      order: questions.length
    };
    onUpdateQuestion(question.id, updatedQuestion);
  };

  return (
    <div className="space-y-6 pb-20">
      <div className="mb-6">
        <h2 className="text-xl font-semibold">Test Questions</h2>
        {invalidQuestions.length > 0 && (
          <div className="mt-2 flex items-center gap-2 text-amber-600 dark:text-amber-400 text-sm">
            <AlertCircle className="h-4 w-4" />
            <span>{invalidQuestions.length} question(s) need completion</span>
          </div>
        )}
      </div>

      <QuestionListSection
        questions={questions}
        onAddQuestion={onAddQuestion}
        onUpdateQuestion={onUpdateQuestion}
        onDeleteQuestion={onDeleteQuestion}
        onRegenerateQuestion={onRegenerateQuestion}
        jobContext={jobContext}
      />
      
      <div className="flex flex-wrap gap-2 justify-center mb-6">
        <Button
          variant="outline"
          onClick={() => setIsQuestionBankOpen(true)}
          className="bg-card hover:bg-card/80"
        >
          <Database className="mr-2 h-4 w-4" />
          Add from Question Bank
        </Button>
        {onAddMultipleQuestions && jobContext && (
          <GenerateAdditionalQuestionsDialog
            testTitle={jobContext.title || ''}
            testDescription={jobContext.description || ''}
            yearsOfExperience={jobContext.yearsOfExperience}
            existingSkills={jobContext.keySkills || []}
            existingQuestions={questions}
            onQuestionsGenerated={onAddMultipleQuestions}
          />
        )}
        <Button onClick={onAddQuestion}>
          <Plus className="mr-2 h-4 w-4" />
          Add Custom Question
        </Button>
      </div>
      
      <div className={`flex ${isMobile ? 'flex-col space-y-3' : 'justify-between'}`}>
        <Button
          variant="outline"
          onClick={onPrevious}
          className={`${isMobile ? 'w-full py-3' : ''} text-foreground`}
          size={isMobile ? "lg" : "default"}
        >
          <ChevronLeft className="mr-2 h-4 w-4" />
          Previous
        </Button>
        
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Button
                  onClick={onNext}
                  disabled={!allQuestionsValid}
                  className={cn(
                    isMobile ? 'w-full py-3' : '',
                    "bg-[#E16702] text-white hover:bg-[#C55A02] transition-colors"
                  )}
                  size={isMobile ? "lg" : "default"}
                >
                  Next
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </TooltipTrigger>
            {!allQuestionsValid && (
              <TooltipContent>
                <p>{getNextButtonTooltip()}</p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </div>

      <QuestionBankDialog
        open={isQuestionBankOpen}
        onOpenChange={setIsQuestionBankOpen}
        onQuestionSelect={handleAddFromQuestionBank}
      />
    </div>
  );
};
